import { Route2g, RouteStop, Toll } from "@/reducers/routes2gSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import RouteStopList, { RouteStopDiff } from "@/components/RouteStopList"
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  FilePlus2,
  FileText,
  MessageSquareText,
  ScanSearch,
  Share2,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  PublicModeChip,
  ReturningAccountBar,
} from "@/components/analyzeOrderOnPage/PublicModeNotice"
import CollapsibleVehicleTollInfo from "@/components/maps/CollapsibleVehicleTollInfo"
import * as React from "react"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { APIProvider } from "@vis.gl/react-google-maps"
import AdHocCommands, {
  AvoidType,
  CarRouteConfig,
  LoadType,
} from "@/api/adHocCommands"
import { RouteInfo } from "@/components/maps/RouteView"
import { Spinner } from "@blueprintjs/core"
import AvoidSelect from "@/components/transportOrder/AvoidSelect"
import LoadTypeSelect from "@/components/transportOrder/LoadTypeSelect"
import { ScrollAreaWithShadow } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import MapView from "../maps/MapView"
import VehicleTypeSelect, {
  carConfigs,
  VEHICLE_TYPE_NAMES,
  VehicleType,
} from "./VehicleTypeSelect"
import { generateShareUrl, saveRouteState } from "@/lib/routeStateUtils"
import { toast } from "sonner"
import { generateRouteName, saveRoute } from "@/lib/saveRoutes"
import { SavedRoutesSelector } from "@/components/ui/saved-routes-selector"
import { ClearRouteDialog } from "@/components/ui/clear-route-dialog"
import ClipboardWriter from "@/lib/clipboardWriter"
import { getOptionsSummaryText } from "@/components/maps/RouteOptionsSelect"
import { useSelector } from "react-redux"
import {
  selectIsHereRouting,
  selectRoutingProvider,
} from "@/reducers/serviceConfigSelectors"
import { generateRouteMessage } from "@/components/analyzeOrderOnPage/utils"
import DriverSelect, {
  SelectedDriverLocation,
} from "@/components/analyzeOrderOnPage/DriverSelect"
import { XmppContext } from "@/stream/xmppClient"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import PlanRouteParser, {
  AlternativeRoute,
  RouteStopWithHandle,
} from "@/lib/planRouteParser"
import AlternativeLeg from "@/components/maps/AlternativeLeg"
import AlternativeRouteSelector from "@/components/maps/AlternativeRouteSelector"
import HoveredRouteHighlight from "@/components/maps/HoveredRouteHighlight"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  clearRouteHandlesFromPassthroughStops,
  fetchCustomRoute,
  handleFetchPerLegAlternatives,
  PerLegAlternatives,
  restorePassthroughs,
} from "@/hooks/useTemporaryRoute"
import SingleStopMarker from "@/components/maps/mapLibre/SingleStopMarker"
import { DropTargetMonitor, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"
import {
  ANY_FORMAT_ORDERS_FLAG,
  isSupportedOrderFile,
  unsupportedOrderFileMessage,
} from "@/lib/orderFileSupport"
import { useIsMobile } from "@/hooks/use-mobile"
import type { ImperativePanelHandle } from "react-resizable-panels"
import type { PlanRouteSnapshot } from "@/components/analyzeOrderOnPage/planRouteTabs"

// Full-view drop target for order documents: dropping a PDF/image anywhere on
// the plan view loads it into the analyze-order flow — same as picking a file
// via "Add transport order". Modeled after the chat's DropZone, single area.
const OrderFileDropZone = ({
  onFileDropped,
}: {
  onFileDropped: (file: File) => void
}) => {
  const { t } = useTranslation()
  const anyFormatEnabled = Boolean(
    useFeatureFlagEnabled(ANY_FORMAT_ORDERS_FLAG),
  )
  const [{ canDrop, isOver }, dropRef] = useDrop<
    { files: FileList },
    unknown,
    { canDrop: boolean; isOver: boolean }
  >(
    () => ({
      accept: [NativeTypes.FILE],
      drop({ files }: { files: FileList }) {
        const file = files?.[0]
        if (!file) return
        if (!isSupportedOrderFile(file, anyFormatEnabled)) {
          toast.warning(unsupportedOrderFileMessage(anyFormatEnabled))
          return
        }
        onFileDropped(file)
      },
      collect(monitor: DropTargetMonitor) {
        // Don't activate for drags that started inside the app (stop reordering).
        const isDragFromApp = (window as any).isDraggingFromApp === true
        return {
          canDrop: monitor.canDrop() && !isDragFromApp,
          isOver: monitor.isOver(),
        }
      },
      // Re-create the drop spec when the flag resolves (PostHog flags are async)
    }),
    [anyFormatEnabled, onFileDropped],
  )

  return (
    <div
      ref={dropRef}
      className="absolute inset-0 z-[1002]"
      style={{
        pointerEvents: canDrop ? "auto" : "none",
        display: canDrop ? "block" : "none",
      }}
    >
      {isOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200/[0.125] backdrop-blur-lg">
          <div className="mx-auto max-w-xs rounded-2xl border-2 border-dashed border-gray-300/30 bg-white/10 px-6 py-8 text-center">
            <div className="mb-4 flex justify-center">
              <svg
                className="h-16 w-16 text-black dark:text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-bold text-black dark:text-white">
              {t("planOrder.planner.dropZone.title")}
            </h2>
            <p className="text-sm text-black dark:text-gray-200">
              {anyFormatEnabled
                ? t("planOrder.planner.dropZone.anyFormat")
                : t("planOrder.planner.dropZone.pdfAndImages")}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

type PlanOrderOnPageProps = {
  onClose: () => void
  onSwitchToAnalyzeOrder?: ({
    avoid,
    loadType,
    vehicleType,
    selectedDriver,
    file,
  }: {
    avoid: AvoidType[]
    loadType: LoadType[]
    vehicleType: VehicleType
    selectedDriver: SelectedDriverLocation | undefined
    // Already-picked document (drag & drop) — skips the file picker.
    file?: File
  }) => void
  onSwitchToTextOrder?: ({
    avoid,
    loadType,
    vehicleType,
    selectedDriver,
  }: {
    avoid: AvoidType[]
    loadType: LoadType[]
    vehicleType: VehicleType
    selectedDriver: SelectedDriverLocation | undefined
  }) => void
  onLoadSavedRoute?: (routeId: string) => void
  onFetchPolylines: (
    stops: RouteStop[],
    avoidOptions: AvoidType[],
    loadType: LoadType[],
    carConfig: CarRouteConfig,
  ) => Promise<
    | [RouteInfo | undefined, RouteStop[], AlternativeRoute[] | undefined]
    | undefined
  >
  sharedRouteState?: any
  sessionId?: string | undefined
  onReset?: () => void
  onRouteModified?: (stopCount: number) => void
  // Route tabs: the bar is shown under the title, the planner reports its
  // state so the tab can restore it, and a name given to the tab goes into
  // the saved-routes history.
  tabBar?: React.ReactNode
  onStateChange?: (snapshot: PlanRouteSnapshot) => void
  routeName?: string
}

const PlanOrderOnPage: React.FC<PlanOrderOnPageProps> = ({
  onFetchPolylines,
  onClose,
  sharedRouteState,
  onSwitchToAnalyzeOrder,
  onSwitchToTextOrder,
  sessionId,
  onLoadSavedRoute,
  onReset,
  onRouteModified,
  tabBar,
  onStateChange,
  routeName,
}: PlanOrderOnPageProps) => {
  const { t } = useTranslation()
  const [messageText, setMessageText] = useState<string | undefined>(
    sharedRouteState?.messageText || "",
  )
  const [copySuccessAnimate, setCopySuccessAnimate] = useState(false)
  const [shareSuccessAnimate, setShareSuccessAnimate] = useState(false)
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false)

  const [temporaryRoute, setTemporaryRoute] = useState<Route2g | undefined>(
    sharedRouteState?.temporaryRoute,
  )
  const [temporaryRouteInfo, setTemporaryRouteInfo] = useState<
    RouteInfo | undefined
  >(sharedRouteState?.temporaryRouteInfo)
  const [selectedAvoidOptions, setSelectedAvoidOptions] = useState<AvoidType[]>(
    sharedRouteState?.selectedAvoidOptions || [],
  )
  const [selectedLoadType, setSelectedLoadType] = useState<LoadType[]>(
    sharedRouteState?.selectedLoadType || [],
  )

  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType>(
    sharedRouteState?.selectedVehicleType || "tir",
  )
  const [selectedDriver, setSelectedDriver] = useState<
    SelectedDriverLocation | undefined
  >(sharedRouteState?.selectedDriver)

  const [driverCarConfig, setDriverCarConfig] = useState<
    CarRouteConfig | undefined
  >(sharedRouteState?.driverCarConfig)
  // Average fuel consumption (l/100km) entered on top of a vehicle preset.
  // It's sent to the GPS with every route request so the returned route
  // alternatives carry a fuel estimate for this vehicle.
  const [presetFuelConsumption, setPresetFuelConsumption] = useState<
    string | undefined
  >(sharedRouteState?.fuelConsumption)
  const [isFetchingPolylines, setIsFetchingPolylines] = useState(false)
  const { client } = useContext(XmppContext)
  // In Marcos mode the route is set in the TMS, so route-sharing is not offered.
  const { configured: isMarcosMode } = useNaviConfig(client?.jid?.domain)

  // Alternative routes state
  const [alternativeRoutes, setAlternativeRoutes] = useState<
    AlternativeRoute[] | undefined
  >(sharedRouteState?.alternativeRoutes)
  const [perLegAlternatives, setPerLegAlternatives] = useState<
    PerLegAlternatives[] | undefined
  >(sharedRouteState?.perLegAlternatives)
  const [hoveredRouteId, setHoveredRouteId] = useState<string | undefined>(
    undefined,
  )

  // Edit mode state - tracks when user is adding/editing stops
  const [isInEditMode, setIsInEditMode] = useState(false)

  // Drag state - tracks when user is dragging the route line to add passthrough
  const [isDraggingRoute, setIsDraggingRoute] = useState(false)

  const alternativeRoutesEnabled =
    Boolean(useFeatureFlagEnabled("alternative-routes")) || window.anonymousXMPP
  const hideDistanceLine = useFeatureFlagEnabled(
    "hide-distance-line-in-route-message",
  )
  const hideStopTimes = Boolean(
    useFeatureFlagEnabled("hide_stop_times_in_route_messages"),
  )
  const isReadDeliveryEnabled = Boolean(useFeatureFlagEnabled("read-delivery"))

  const getCurrentCarConfig = useCallback((): CarRouteConfig => {
    // Use driver's car config if available (either from selected driver or from shared route)
    if (driverCarConfig) {
      return driverCarConfig
    }
    const preset = carConfigs[selectedVehicleType]
    return presetFuelConsumption
      ? { ...preset, fuelConsumption: presetFuelConsumption }
      : preset
  }, [driverCarConfig, selectedVehicleType, presetFuelConsumption])

  // Check if we should use driver's config
  const isUsingDriverConfig = Boolean(driverCarConfig)
  const haveFirstStopAsDriverLocation =
    temporaryRoute &&
    temporaryRoute.stops &&
    temporaryRoute.stops.length > 0 &&
    temporaryRoute.stops[0].action === "from_driver_location"

  // Initialize state from shared route data
  useEffect(() => {
    if (sharedRouteState) {
      setTemporaryRoute(sharedRouteState.temporaryRoute)
      setTemporaryRouteInfo(sharedRouteState.temporaryRouteInfo)
      setSelectedAvoidOptions(sharedRouteState.selectedAvoidOptions || [])
      setSelectedLoadType(sharedRouteState.selectedLoadType || [])
      setSelectedVehicleType(sharedRouteState.selectedVehicleType || "tir")
      // Only a route tab remembers the driver; a route from the history has
      // none, so a driver picked for the previous route does not stick.
      setSelectedDriver(sharedRouteState.selectedDriver)
      setDriverCarConfig(sharedRouteState.driverCarConfig)
      setPresetFuelConsumption(sharedRouteState.fuelConsumption)
      setMessageText(sharedRouteState.messageText || "")

      // Restore alternatives if available
      if (sharedRouteState.alternativeRoutes) {
        setAlternativeRoutes(sharedRouteState.alternativeRoutes)
      }
      if (sharedRouteState.perLegAlternatives) {
        setPerLegAlternatives(sharedRouteState.perLegAlternatives)
      }
    }
  }, [sharedRouteState])

  // Fetch alternatives for old routes that don't have them saved
  useEffect(() => {
    if (!sharedRouteState || !alternativeRoutesEnabled) return
    if (sharedRouteState.alternativeRoutes) return // Already has alternatives

    const hasStops =
      sharedRouteState.temporaryRoute?.stops &&
      sharedRouteState.temporaryRoute.stops.length >= 2

    if (!hasStops || !window.XMPP || !client) return

    const vehicleType: VehicleType =
      sharedRouteState.selectedVehicleType || "tir"
    const carConfig: CarRouteConfig = sharedRouteState.driverCarConfig || {
      ...carConfigs[vehicleType],
      fuelConsumption: sharedRouteState.fuelConsumption,
    }

    // Fetch alternatives for the saved route
    onFetchPolylines(
      sharedRouteState.temporaryRoute.stops,
      sharedRouteState.selectedAvoidOptions || [],
      sharedRouteState.selectedLoadType || [],
      carConfig,
    ).then((result) => {
      if (result) {
        const [, stops, alternatives] = result
        if (alternatives && alternatives.length > 0) {
          setAlternativeRoutes(alternatives)

          // Auto-select the first alternative so it appears selected in the UI
          const firstStop = sharedRouteState.temporaryRoute.stops[0]
          const defaultAlt = alternatives[0]
          if (defaultAlt && firstStop) {
            setTemporaryRoute((prev) =>
              prev
                ? {
                    ...prev,
                    stops: [firstStop, ...defaultAlt.stops],
                    routeId: defaultAlt.type,
                    tolls: defaultAlt.tolls || [],
                  }
                : prev,
            )
            setTemporaryRouteInfo(defaultAlt.routeInfo)
          }

          // Fetch per-leg alternatives
          handleFetchPerLegAlternatives(
            stops,
            `on-page@${window.XMPP!.options.domain}`,
            {
              avoid: sharedRouteState.selectedAvoidOptions || [],
              loadType: sharedRouteState.selectedLoadType || [],
              carConfig,
            },
          ).then((perLegResults) => {
            setPerLegAlternatives(perLegResults)
          })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedRouteState, alternativeRoutesEnabled, client])

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Phone only: the driver message starts folded away so the stops get the
  // panel; Share/Copy stay visible, the textarea opens on demand.
  const [isMessageOpen, setIsMessageOpen] = useState(false)
  const topPanelRef = useRef<ImperativePanelHandle>(null)
  const panelHeaderRef = useRef<HTMLDivElement>(null)
  const messageAreaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (window.XMPP) {
      // Don't reset if we have a shared route state
      if (sessionId !== undefined || sharedRouteState) return
      setTemporaryRoute(undefined)
      setTemporaryRouteInfo(undefined)
      setSelectedAvoidOptions([])
      setSelectedLoadType([])
      setSelectedVehicleType("tir")
      setPresetFuelConsumption(undefined)
      setSelectedDriver(undefined)
      setMessageText("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const handleSaveRoute = useCallback(() => {
    if (
      window.XMPP &&
      temporaryRoute &&
      temporaryRoute.stops &&
      temporaryRoute.stops.length >= 2
    ) {
      saveRoute(
        temporaryRoute,
        temporaryRouteInfo,
        selectedAvoidOptions,
        selectedLoadType,
        selectedVehicleType,
        messageText,
        {
          currentStep: "map",
          driverCarConfig: isUsingDriverConfig ? driverCarConfig : undefined,
          fuelConsumption: isUsingDriverConfig
            ? undefined
            : presetFuelConsumption,
          customName: `[${isUsingDriverConfig ? "Driver" : VEHICLE_TYPE_NAMES[selectedVehicleType ?? "tir"]}] - ${routeName ? `${routeName} · ` : ""}${generateRouteName(temporaryRoute.stops)}`,
          alternativeRoutes,
          perLegAlternatives,
        },
      )
    }
  }, [
    driverCarConfig,
    isUsingDriverConfig,
    presetFuelConsumption,
    messageText,
    selectedAvoidOptions,
    selectedLoadType,
    selectedVehicleType,
    temporaryRoute,
    temporaryRouteInfo,
    alternativeRoutes,
    perLegAlternatives,
    routeName,
  ])

  // Keep ref to latest save function for cleanup
  const saveRouteRef = useRef(handleSaveRoute)
  useEffect(() => {
    saveRouteRef.current = handleSaveRoute
  }, [handleSaveRoute])

  // Save route on unmount (handles sidebar navigation, tab switching, etc.)
  useEffect(() => {
    return () => {
      saveRouteRef.current()
    }
  }, [])

  // Every calculated route goes to the history right away, so routes that
  // are edited in place (rather than cleared or left) are kept as well. The
  // save waits a moment because the message text is regenerated in a later
  // render than the route itself, and typing in it should not write on every
  // keystroke.
  useEffect(() => {
    if (!temporaryRouteInfo) return
    const timer = setTimeout(() => saveRouteRef.current(), 1000)
    return () => clearTimeout(timer)
  }, [temporaryRouteInfo, messageText])

  // A route tab keeps whatever the planner shows, so switching back to it
  // brings the route, the options and the message back as they were.
  const onStateChangeRef = useRef(onStateChange)
  useEffect(() => {
    onStateChangeRef.current = onStateChange
  }, [onStateChange])
  useEffect(() => {
    onStateChangeRef.current?.({
      temporaryRoute,
      temporaryRouteInfo,
      selectedAvoidOptions,
      selectedLoadType,
      selectedVehicleType,
      selectedDriver,
      driverCarConfig,
      fuelConsumption: driverCarConfig ? undefined : presetFuelConsumption,
      messageText,
      alternativeRoutes,
      perLegAlternatives,
    })
  }, [
    temporaryRoute,
    temporaryRouteInfo,
    selectedAvoidOptions,
    selectedLoadType,
    selectedVehicleType,
    selectedDriver,
    driverCarConfig,
    presetFuelConsumption,
    messageText,
    alternativeRoutes,
    perLegAlternatives,
  ])

  // Handle close with save dialog
  const handleClose = useCallback(() => {
    // Show save dialog when closing (only in product context)
    handleSaveRoute()
    // If no route to save, continue with close action immediately
    onClose()
  }, [handleSaveRoute, onClose])

  // Handle confirming route clear
  const handleConfirmClear = useCallback(() => {
    handleSaveRoute()
    window.analytics?.track("Anonymous_RouteClearConfirmed", {
      stopCount: temporaryRoute?.stops?.length ?? 0,
      source: "plan",
    })
    setTemporaryRoute(undefined)
    setTemporaryRouteInfo(undefined)
    setSelectedAvoidOptions([])
    setSelectedLoadType([])
    setSelectedVehicleType("tir")
    setPresetFuelConsumption(undefined)
    setSelectedDriver(undefined)
    setMessageText("")
    setAlternativeRoutes(undefined)
    setPerLegAlternatives(undefined)
    onReset?.()
  }, [handleSaveRoute, onReset])

  const copyMessage = (e: React.MouseEvent) => {
    e.stopPropagation()
    ClipboardWriter.executeAndWriteResult(async () => messageText || "")
    window.analytics?.track(
      window.anonymousXMPP
        ? "Anonymous_MessageCopied"
        : "OrderOnPage_MessageCopied",
      {
        messageText,
      },
    )

    setCopySuccessAnimate(true)
    setTimeout(() => {
      setCopySuccessAnimate(false)
    }, 700)
  }

  const saveAndGenerateShareUrl = useCallback(async () => {
    setIsGeneratingShareLink(true)
    try {
      const stateId = await saveRouteState({
        // @ts-ignore
        temporaryRoute: {
          ...temporaryRoute,
          stops:
            temporaryRoute?.stops?.map((stop) => ({
              ...stop,
              action:
                stop.action === "from_driver_location" ? "other" : stop.action,
              by: undefined, // Remove the by field
            })) || [],
        },
        temporaryRouteInfo,
        selectedAvoidOptions,
        selectedLoadType,
        selectedVehicleType,
        driverCarConfig: isUsingDriverConfig ? driverCarConfig : undefined,
        fuelConsumption: isUsingDriverConfig
          ? undefined
          : presetFuelConsumption,
        messageText,
        alternativeRoutes,
        perLegAlternatives,
      })

      const shareUrl = generateShareUrl(stateId)

      window.analytics?.track(
        window.anonymousXMPP
          ? "Anonymous_RouteShared"
          : "OrderOnPage_RouteShared",
        {
          stateId,
          shareUrl,
          routeStopsCount: temporaryRoute?.stops?.length || 0,
        },
      )

      setShareSuccessAnimate(true)
      setTimeout(() => {
        setShareSuccessAnimate(false)
      }, 700)
      toast(t("planOrder.routeToasts.shareLinkCopied"))
      return shareUrl
    } catch (error) {
      console.error("Failed to share route:", error)
      toast.error(t("planOrder.routeToasts.shareLinkFailed"))
    } finally {
      setIsGeneratingShareLink(false)
    }
  }, [
    driverCarConfig,
    isUsingDriverConfig,
    presetFuelConsumption,
    messageText,
    selectedAvoidOptions,
    selectedLoadType,
    selectedVehicleType,
    temporaryRoute,
    temporaryRouteInfo,
    alternativeRoutes,
    perLegAlternatives,
    t,
  ])

  const shareRoute = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      ClipboardWriter.executeAndWriteResult(saveAndGenerateShareUrl)
    },
    [saveAndGenerateShareUrl],
  )

  const fetchNewTemporaryRoute = useCallback(
    async ({
      updatedRoute,
      avoidOptions,
      loadType,
      carConfig,
      passthroughStops,
    }: {
      updatedRoute: Route2g
      avoidOptions: AvoidType[]
      loadType: LoadType[]
      carConfig: CarRouteConfig
      // The same stops with the dispatcher's passthroughs, which
      // `updatedRoute` leaves out so the alternatives start clean. The route
      // through them is then selected as the Custom route.
      passthroughStops?: RouteStop[]
    }) => {
      setIsFetchingPolylines(true)
      try {
        const [routeData, stops, alternatives] =
          (await onFetchPolylines(
            updatedRoute.stops,
            avoidOptions,
            loadType,
            carConfig,
          )) || []
        if (routeData) {
          const throughPassthroughs =
            passthroughStops && stops
              ? restorePassthroughs(passthroughStops, stops)
              : undefined
          // Should it fail, the route stays without the passthroughs.
          const customRoute = throughPassthroughs
            ? await fetchCustomRoute(
                throughPassthroughs,
                `on-page@${window.XMPP!.options.domain}`,
                { ...carConfig, avoid: avoidOptions, loadType },
              ).catch((e) => {
                console.error(e)
                return undefined
              })
            : undefined
          setTemporaryRoute({
            ...updatedRoute,
            stops: customRoute?.stops ?? stops ?? updatedRoute.stops,
            tolls:
              (customRoute
                ? customRoute.route.tolls
                : routeData.routes?.[0]?.tolls) || [],
          })
          setTemporaryRouteInfo(customRoute?.route.routeInfo ?? routeData)
          if (alternativeRoutesEnabled && alternatives) {
            setAlternativeRoutes(
              customRoute ? [customRoute.route, ...alternatives] : alternatives,
            )
            // Fetch per-leg alternatives
            if (stops && stops.length >= 2) {
              const perLegResults = await handleFetchPerLegAlternatives(
                stops,
                `on-page@${window.XMPP!.options.domain}`,
                { avoid: avoidOptions, loadType, carConfig },
              )
              console.log("[DEBUG] Per-leg alternatives:", perLegResults)
              setPerLegAlternatives(perLegResults)
            }
          }
        } else if (stops) {
          setTemporaryRoute({
            ...updatedRoute,
            stops: stops,
            tolls: [],
          })
          setTemporaryRouteInfo(undefined)
          setAlternativeRoutes(undefined)
          setPerLegAlternatives(undefined)
        }
      } catch (e) {
        console.error(e)
        window.analytics?.track("Anonymous_RouteCalculationFailed", {
          error: e instanceof Error ? e.message : String(e),
          stopCount: updatedRoute.stops.length,
          source: "plan",
        })
      } finally {
        setIsFetchingPolylines(false)
      }
    },
    [onFetchPolylines, alternativeRoutesEnabled],
  )

  const handleTemporaryRouteUpdate = useCallback(
    async (updatedRoute: Route2g, diff: RouteStopDiff) => {
      // Notify parent that route was modified (for lead capture)
      onRouteModified?.(updatedRoute.stops.length)

      if (updatedRoute.stops.length === 0) {
        setMessageText("")
        setTemporaryRoute(undefined)
        setTemporaryRouteInfo(undefined)
        setSelectedDriver(undefined)
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)
        return
      }

      // Only 1 stop - can't have a route, clear route info but keep the stop
      if (updatedRoute.stops.length === 1) {
        updatedRoute.stops[0].is_temporary = true
        setTemporaryRoute(updatedRoute)
        setTemporaryRouteInfo(undefined)
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)
        // Update driver selection if first stop changed
        if (selectedDriver) {
          const firstStop = updatedRoute.stops[0]
          if (firstStop.action !== "from_driver_location") {
            setSelectedDriver(undefined)
          }
        }
        setMessageText(
          generateRouteMessage({
            route: updatedRoute,
            hideDistanceLine,
            hideStopTimes,
          }),
        )
        return
      }

      // Only action changed (not location) - update route and message without fetching
      if (
        diff.hasActionOnlyChanges &&
        !diff.hasNonPassthroughChanges &&
        !diff.hasPassthroughChanges
      ) {
        setTemporaryRoute(updatedRoute)
        setMessageText(
          generateRouteMessage({
            route: updatedRoute,
            hideDistanceLine,
            hideStopTimes,
          }),
        )
        return
      }

      const carConfig = getCurrentCarConfig()

      // Check if we have non-passthrough changes WITHOUT passthrough changes
      // This means: stops added/removed/reordered, or avoid/loadType changed, but no passthrough modifications
      const hasNonPassthroughChangesOnly =
        (diff.hasNonPassthroughChanges ||
          diff.hasAvoidChanges ||
          diff.hasLoadTypeChanges ||
          diff.hasOrderChanges) &&
        !diff.hasPassthroughChanges

      if (hasNonPassthroughChangesOnly) {
        if (diff.hasOrderChanges) {
          window.analytics?.track("Anonymous_StopReordered", {
            stopCount: updatedRoute.stops.length,
            source: "plan",
          })
        }
        // Branch 1: Non-passthrough changes - reset and fetch fresh alternatives
        // Clear route handles and leave passthroughs out for a clean slate;
        // the passthroughs come back as the Custom route, as in Branch 2
        setAlternativeRoutes(undefined)
        setPerLegAlternatives(undefined)

        const resetStops = updatedRoute.stops.map((stop) => ({
          ...stop,
          route_handle: undefined,
          addressUpdated: true,
        }))
        const sanitizedStops = resetStops.filter(
          (stop) => stop.action !== "passthrough",
        )

        if (sanitizedStops.length === 0) {
          setMessageText("")
          setTemporaryRoute(undefined)
          setTemporaryRouteInfo(undefined)
          return
        }

        // Use fetchNewTemporaryRoute with sanitized stops
        await fetchNewTemporaryRoute({
          updatedRoute: { ...updatedRoute, stops: sanitizedStops },
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig,
          passthroughStops: resetStops,
        })
      } else if (diff.hasPassthroughChanges && alternativeRoutesEnabled) {
        // Branch 2: Has passthrough changes - create custom route while preserving existing alternatives
        setIsFetchingPolylines(true)
        try {
          let sanitizedStops = updatedRoute.stops
          if (diff.modified.length > 0 || diff.added.length > 0) {
            sanitizedStops = clearRouteHandlesFromPassthroughStops(
              updatedRoute.stops,
            )
          } else if (diff.removed.length > 0 && temporaryRoute) {
            const stopAfterRemovedStop =
              temporaryRoute.stops[diff.removed[0].index + 1]
            sanitizedStops = sanitizedStops.map((stop) => ({
              ...stop,
              route_handle:
                stop.id === stopAfterRemovedStop?.id
                  ? undefined
                  : (stop as RouteStopWithHandle).route_handle,
            }))
          }

          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            `on-page@${window.XMPP!.options.domain}`,
            sanitizedStops,
            {
              ...carConfig,
              avoid: selectedAvoidOptions,
              loadType: selectedLoadType,
            },
          )

          const parsedRoute = PlanRouteParser.parse(result)
          if (parsedRoute) {
            const { stops, alternativeRoutes: newAlternatives } = parsedRoute

            setTemporaryRoute({
              ...updatedRoute,
              stops: [sanitizedStops[0], ...stops],
              tolls: newAlternatives?.[0]?.tolls || [],
            })

            if (newAlternatives?.[0]) {
              const customRoute = newAlternatives[0]
              customRoute.type = "Custom"
              customRoute.routeInfo.routes[0].isCustom = true
              customRoute.routeInfo.routes[0].id = "Custom"
              customRoute.routeInfo.routes[0].label = "Custom"

              const currentAlternativesWithoutCustom = (
                alternativeRoutes || []
              ).filter((alt) => !alt.routeInfo.routes[0].isCustom)

              setAlternativeRoutes([
                customRoute,
                ...currentAlternativesWithoutCustom,
              ])
              setTemporaryRouteInfo(customRoute.routeInfo)
            } else if (parsedRoute.route) {
              setTemporaryRouteInfo(parsedRoute.route)
            }
          }
        } catch (e) {
          console.error(e)
        } finally {
          setIsFetchingPolylines(false)
        }
      } else {
        // Branch 3: No special handling needed (passthrough changes with feature disabled, or no changes)
        await fetchNewTemporaryRoute({
          updatedRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig,
        })
      }
      // Update driver selection if first stop changed
      if (selectedDriver) {
        const firstStop = updatedRoute.stops[0]
        if (firstStop.action !== "from_driver_location") {
          setSelectedDriver(undefined)
        }
      }
    },
    [
      fetchNewTemporaryRoute,
      getCurrentCarConfig,
      selectedAvoidOptions,
      selectedLoadType,
      selectedDriver,
      alternativeRoutesEnabled,
      temporaryRoute,
      alternativeRoutes,
      onRouteModified,
    ],
  )

  // Tracks the route that was last used to generate the message, so a flag
  // resolution alone (without an actual route change) doesn't overwrite the
  // user's edits.
  const lastGeneratedRouteRef = useRef<Route2g | undefined>(undefined)

  useEffect(() => {
    if (!temporaryRoute) {
      setMessageText("")
      lastGeneratedRouteRef.current = undefined
      return
    }

    if (hideDistanceLine === undefined) return
    if (lastGeneratedRouteRef.current === temporaryRoute) return

    // Check if sharedRouteState exists and stops are the same
    const areStopsSame =
      sharedRouteState?.temporaryRoute?.stops &&
      temporaryRoute.stops.length ===
        sharedRouteState.temporaryRoute.stops.length &&
      temporaryRoute.stops.every(
        (stop, index) =>
          stop.id === sharedRouteState.temporaryRoute.stops[index].id &&
          stop.remaining_distance ===
            sharedRouteState.temporaryRoute.stops[index].remaining_distance,
      )

    if (!areStopsSame || textareaRef.current?.value?.length === 0) {
      lastGeneratedRouteRef.current = temporaryRoute
      setMessageText(
        generateRouteMessage({
          route: temporaryRoute,
          hideDistanceLine,
          hideStopTimes,
        }),
      )
    }
  }, [temporaryRoute, sharedRouteState, hideDistanceLine, hideStopTimes])

  const routeContainerRef = useRef<HTMLDivElement>(null)

  const handleAvoidSelectClose = useCallback(
    async (newAvoidOptions: AvoidType[]) => {
      const hasChanged =
        selectedAvoidOptions.length !== newAvoidOptions.length ||
        !selectedAvoidOptions.every((item) => newAvoidOptions.includes(item))
      if (!hasChanged) return
      window.analytics?.track("Anonymous_AvoidOptionsChanged", {
        avoidOptions: newAvoidOptions,
        source: "plan",
      })
      setSelectedAvoidOptions(newAvoidOptions)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: temporaryRoute,
          avoidOptions: newAvoidOptions,
          loadType: selectedLoadType,
          carConfig: getCurrentCarConfig(),
        })
      }
    },
    [
      selectedAvoidOptions,
      temporaryRoute,
      fetchNewTemporaryRoute,
      selectedLoadType,
      getCurrentCarConfig,
    ],
  )

  const handleLoadTypeSelectClose = useCallback(
    async (newLoadType: LoadType[]) => {
      const hasChanged =
        selectedLoadType.length !== newLoadType.length ||
        !selectedLoadType.every((item) => newLoadType.includes(item))
      if (!hasChanged) return
      window.analytics?.track("Anonymous_LoadTypeChanged", {
        loadType: newLoadType,
        source: "plan",
      })
      setSelectedLoadType(newLoadType)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: temporaryRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: newLoadType,
          carConfig: getCurrentCarConfig(),
        })
      }
    },
    [
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      temporaryRoute,
      getCurrentCarConfig,
    ],
  )

  const handleVehicleTypeChange = useCallback(
    async (vehicle: VehicleType) => {
      if (vehicle === selectedVehicleType) return
      window.analytics?.track("Anonymous_VehicleTypeChanged", {
        vehicleType: vehicle,
        source: "plan",
      })
      setSelectedVehicleType(vehicle)
      setDriverCarConfig(undefined)
      setPresetFuelConsumption(undefined)
      setSelectedDriver(undefined)
      if (temporaryRoute) {
        const updatedRoute = {
          ...temporaryRoute,
          stops: temporaryRoute?.stops?.filter(
            (stop) => stop.action !== "from_driver_location",
          ),
        }
        if (updatedRoute) {
          await fetchNewTemporaryRoute({
            updatedRoute: updatedRoute,
            avoidOptions: selectedAvoidOptions,
            loadType: selectedLoadType,
            carConfig: carConfigs[vehicle],
          })
        }
      }
    },
    [
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedDriver,
      selectedLoadType,
      selectedVehicleType,
      temporaryRoute,
    ],
  )

  const handleFuelConsumptionChange = useCallback(
    async (value: string | undefined) => {
      setPresetFuelConsumption(value)
      window.analytics?.track("Anonymous_FuelConsumptionChanged", {
        fuelConsumption: value,
        vehicleType: selectedVehicleType,
        source: "plan",
      })
      // Fuel estimates come from the GPS, so the route (and its alternatives)
      // has to be recalculated with the new consumption.
      if (temporaryRoute && temporaryRoute.stops.length >= 2) {
        const preset = carConfigs[selectedVehicleType]
        await fetchNewTemporaryRoute({
          updatedRoute: temporaryRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig: value ? { ...preset, fuelConsumption: value } : preset,
        })
      }
    },
    [
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      selectedVehicleType,
      temporaryRoute,
    ],
  )

  const handleDriverSelection = useCallback(
    async (driverLocation: SelectedDriverLocation | undefined) => {
      let carConfig: CarRouteConfig | undefined = undefined

      setSelectedDriver(driverLocation)
      // A driver's car config replaces the preset, including its consumption.
      setPresetFuelConsumption(undefined)
      if (!driverLocation || !driverLocation.location) {
        setDriverCarConfig(carConfig)
        // Driver unselected - remove driver location from route if it exists
        if (temporaryRoute && temporaryRoute.stops.length > 0) {
          // Check if first stop is driver location (has special marker)
          const firstStop = temporaryRoute.stops[0]
          if (firstStop.by === selectedDriver?.driverJid) {
            // Remove driver stop and update route
            const newStops =
              temporaryRoute.stops
                .slice(1)
                .filter((stop) => stop.action !== "passthrough")
                .map((stop) => ({
                  ...stop,
                  route_handle: undefined,
                  addressUpdated: true,
                })) ?? []

            // If no stops left after removing driver, clear everything
            if (newStops.length === 0) {
              setMessageText("")
              setTemporaryRoute(undefined)
              setTemporaryRouteInfo(undefined)
              setAlternativeRoutes(undefined)
              setPerLegAlternatives(undefined)
              return
            }

            const updatedRoute = { ...temporaryRoute, stops: newStops }
            setTemporaryRoute(updatedRoute)

            // If only 1 stop left, clear route info but don't fetch
            if (newStops.length === 1) {
              setTemporaryRouteInfo(undefined)
              setAlternativeRoutes(undefined)
              setPerLegAlternatives(undefined)
              return
            }

            await fetchNewTemporaryRoute({
              updatedRoute: updatedRoute,
              avoidOptions: selectedAvoidOptions,
              loadType: selectedLoadType,
              carConfig: carConfigs["tir"],
            })
          }
        }
        return
      }

      // Driver selected - add their location as first stop
      window.analytics?.track("Anonymous_DriverSelected", { source: "plan" })
      const driverLocation_converted = driverLocation.location
      if (!driverLocation_converted.lat || !driverLocation_converted.lon) {
        console.warn("Driver location missing coordinates")
        return
      }

      const driverStop: RouteStop = {
        id: `driver-${driverLocation.driverJid}`,
        driverInitials: driverLocation.driverInitials,
        by: driverLocation.driverJid,
        location: {
          ...driverLocation_converted,
          formattedAddress: `${driverLocation_converted.lat},${driverLocation_converted.lon}`,
          lat: driverLocation_converted.lat,
          lon: driverLocation_converted.lon,
        },
        action: "from_driver_location",
        is_temporary: true,
      }

      let newStops: RouteStop[]
      if (temporaryRoute && temporaryRoute.stops.length > 0) {
        // Determine which stops to keep (skip first stop if it's a driver location)
        const firstStop = temporaryRoute.stops[0]
        const shouldSkipFirst = firstStop.by // First stop is a driver location
        const existingStops = temporaryRoute.stops
          .slice(shouldSkipFirst ? 1 : 0)
          .filter((stop) => stop.action !== "passthrough")
          .map((stop) => ({
            ...stop,
            route_handle: undefined,
            addressUpdated: true,
          }))

        newStops = [driverStop, ...existingStops]
      } else {
        // No existing route, create new with driver location
        newStops = [driverStop]
      }

      const updatedRoute: Route2g = {
        jid: temporaryRoute?.jid || "on-page",
        stops: newStops,
        tolls: temporaryRoute?.tolls || [],
      }
      if (driverLocation?.driverJid) {
        carConfig = await XmppPubSubApi.fetchCarConfig(
          window.XMPP!,
          driverLocation?.driverJid,
        )
        setDriverCarConfig(carConfig)
      }

      setTemporaryRoute(updatedRoute)
      if (temporaryRoute) {
        await fetchNewTemporaryRoute({
          updatedRoute: updatedRoute,
          avoidOptions: selectedAvoidOptions,
          loadType: selectedLoadType,
          carConfig: carConfig || getCurrentCarConfig(),
        })
      }
    },
    [
      temporaryRoute,
      selectedDriver?.driverJid,
      fetchNewTemporaryRoute,
      selectedAvoidOptions,
      selectedLoadType,
      getCurrentCarConfig,
    ],
  )

  const handleDriverTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(e.target.value)
    },
    [],
  )

  const handleSwitchToAnalyzeOrder = useCallback(() => {
    onSwitchToAnalyzeOrder?.({
      avoid: selectedAvoidOptions,
      loadType: selectedLoadType,
      vehicleType: selectedVehicleType,
      selectedDriver: selectedDriver,
    })
  }, [
    onSwitchToAnalyzeOrder,
    selectedAvoidOptions,
    selectedDriver,
    selectedLoadType,
    selectedVehicleType,
  ])

  const handleOrderFileDropped = useCallback(
    (file: File) => {
      onSwitchToAnalyzeOrder?.({
        avoid: selectedAvoidOptions,
        loadType: selectedLoadType,
        vehicleType: selectedVehicleType,
        selectedDriver: selectedDriver,
        file,
      })
    },
    [
      onSwitchToAnalyzeOrder,
      selectedAvoidOptions,
      selectedDriver,
      selectedLoadType,
      selectedVehicleType,
    ],
  )

  const handleSwitchToTextOrder = useCallback(() => {
    onSwitchToTextOrder?.({
      avoid: selectedAvoidOptions,
      loadType: selectedLoadType,
      vehicleType: selectedVehicleType,
      selectedDriver: selectedDriver,
    })
  }, [
    onSwitchToTextOrder,
    selectedAvoidOptions,
    selectedDriver,
    selectedLoadType,
    selectedVehicleType,
  ])

  // Handle edit mode changes from RouteStopList
  const handleEditModeChange = useCallback((isEditing: boolean) => {
    setIsInEditMode(isEditing)
  }, [])

  // Handle route drag state changes from MapView
  const handleRouteDragChange = useCallback((isDragging: boolean) => {
    setIsDraggingRoute(isDragging)
  }, [])

  // Alternative routes handlers
  const handleRouteSelect = useCallback(
    (routeType: string) => {
      const selectedAlternative = alternativeRoutes?.find(
        (alt) => alt.type === routeType,
      )
      if (
        temporaryRoute &&
        temporaryRoute.stops.length > 0 &&
        selectedAlternative &&
        selectedAlternative.stops &&
        selectedAlternative.routeInfo
      ) {
        // Preserve the first stop (user's starting point) which is not included in alternatives
        const firstStop = temporaryRoute.stops[0]
        setTemporaryRoute({
          ...temporaryRoute,
          stops: [firstStop, ...selectedAlternative.stops],
          routeId: selectedAlternative.type,
        })
        setTemporaryRouteInfo(selectedAlternative.routeInfo)

        if (selectedAlternative.carRouteConfigAvoid !== undefined) {
          setSelectedAvoidOptions(selectedAlternative.carRouteConfigAvoid)
        }

        window.analytics?.track(
          window.anonymousXMPP
            ? "Anonymous_AlternativeRouteSelected"
            : "OrderOnPage_AlternativeRouteSelected",
          {
            routeType,
            distance: selectedAlternative.routeInfo.routes[0]?.distance,
            fuel: selectedAlternative.fuel,
          },
        )
      }
    },
    [alternativeRoutes, setSelectedAvoidOptions, temporaryRoute],
  )

  const handleRouteHover = useCallback((routeId: string | undefined) => {
    setHoveredRouteId(routeId)
  }, [])

  // Calculate selected route handles (skip first stop as it's manually prepended and has no route_handle)
  const selectedRouteHandles = useMemo(
    () =>
      (temporaryRoute?.stops as RouteStopWithHandle[] | undefined)
        ?.slice(1)
        .map((stop) => stop.route_handle)
        .filter((handle) => handle !== undefined) || [],
    [temporaryRoute?.stops],
  )

  // Calculate alternatives with legs for rendering
  // Filter out alternatives whose stop count differs from clean alternatives (e.g., custom
  // routes with passthroughs have extra stops and their legs don't align with clean routes)
  const alternativesWithLegs = useMemo(() => {
    if (!alternativeRoutes) return undefined

    const canonicalStopCount = alternativeRoutes.find(
      (alt) => !alt.routeInfo.routes[0]?.isCustom,
    )?.stops.length

    return alternativeRoutes
      .filter(
        (alt) =>
          canonicalStopCount === undefined ||
          alt.stops.length === canonicalStopCount,
      )
      .map((alternativeRoute, index) => ({
        type: alternativeRoute.type,
        uniqueId: `${alternativeRoute.type}-${index}`,
        legs: alternativeRoute.stops.map((stop) => ({
          stop,
          route_handle: stop.route_handle,
          polyline: stop.polyline,
        })),
      }))
  }, [alternativeRoutes])

  // Calculate per-leg alternatives for map rendering
  const perLegAlternativesForMap = useMemo(
    () =>
      perLegAlternatives?.flatMap((legGroup) => {
        return legGroup.alternatives.map((alternative, altIndex) => ({
          route_handle: alternative.stops[0]?.route_handle,
          type: alternative.type,
          uniqueId: `leg-${legGroup.fromStopIndex}-${legGroup.toStopIndex}-${alternative.type}-${altIndex}`,
          fromStopIndex: legGroup.fromStopIndex,
          toStopIndex: legGroup.toStopIndex,
          tolls: alternative.tolls,
          fuel: alternative.fuel,
          legs: alternative.stops.map((stop) => ({
            stop,
            route_handle: stop.route_handle,
            polyline: stop.polyline,
          })),
        }))
      }),
    [perLegAlternatives],
  )

  // Calculate unique alternative legs for rendering
  const uniqueAlternativeLegs = useMemo(() => {
    const allLegs: Array<{
      leg: { stop: RouteStopWithHandle; route_handle: string; polyline: string }
      legIndex: number
      id: string
      routeId: string
      tolls: Toll[]
      fuel: number
    }> = []

    // Collect legs from whole-route alternatives
    if (alternativesWithLegs && alternativesWithLegs.length > 0) {
      alternativesWithLegs.forEach((alternative) => {
        alternative.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex,
              id: leg.route_handle,
              routeId: alternative.type,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Collect legs from per-leg alternatives
    if (perLegAlternativesForMap) {
      perLegAlternativesForMap.forEach((legAlt) => {
        legAlt.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex: legAlt.fromStopIndex ?? legIndex,
              id: leg.route_handle,
              routeId: legAlt.uniqueId,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Deduplicate by route_handle
    const seenHandles = new Set<string>()
    return allLegs.filter((legData) => {
      if (seenHandles.has(legData.leg.route_handle)) {
        return false
      }
      seenHandles.add(legData.leg.route_handle)
      return true
    })
  }, [alternativesWithLegs, perLegAlternativesForMap, selectedRouteHandles])

  // Determine which legs have passthrough stops (hide alternatives for these legs)
  const legsWithPassthroughs = useMemo(() => {
    if (!temporaryRoute?.stops || temporaryRoute.stops.length < 2)
      return new Set<number>()

    const stops = temporaryRoute.stops
    const result = new Set<number>()

    const nonPassthroughStopIndices: number[] = []
    stops.forEach((stop, index) => {
      if (stop.action !== "passthrough") {
        nonPassthroughStopIndices.push(index)
      }
    })

    for (let leg = 0; leg < nonPassthroughStopIndices.length - 1; leg++) {
      const fromIdx = nonPassthroughStopIndices[leg]
      const toIdx = nonPassthroughStopIndices[leg + 1]
      if (toIdx - fromIdx > 1) {
        result.add(leg)
      }
    }

    return result
  }, [temporaryRoute?.stops])

  // Handle clicking on alternative leg to create custom route
  const handleLegClick = useCallback(
    async (route_handle: string, legIndex: number) => {
      if (!temporaryRoute || !alternativeRoutes) {
        return
      }

      // Find the leg by route_handle
      const leg = uniqueAlternativeLegs.find(
        (legData) => legData.leg.route_handle === route_handle,
      )

      if (!leg) {
        console.error("Leg not found for route_handle:", route_handle)
        return
      }

      const currentStops = temporaryRoute.stops as RouteStopWithHandle[]
      const firstStop = currentStops[0]

      // The remaining stops (excluding first stop)
      const remainingStops = currentStops.slice(1)

      // Find actual indices of non-passthrough stops in remainingStops
      const nonPassthroughIndices: number[] = []
      remainingStops.forEach((stop, index) => {
        if (stop.action !== "passthrough") {
          nonPassthroughIndices.push(index)
        }
      })

      // legIndex is the logical (non-passthrough) stop index within the alternatives
      const actualStopIndex = nonPassthroughIndices[legIndex]

      if (actualStopIndex === undefined) {
        console.error(
          "Invalid leg index:",
          legIndex,
          "non-passthrough stops count:",
          nonPassthroughIndices.length,
        )
        return
      }

      // Find the previous non-passthrough stop (START of the segment)
      const prevStopLogicalIndex = legIndex - 1
      const prevStopActualIndex =
        prevStopLogicalIndex >= 0
          ? nonPassthroughIndices[prevStopLogicalIndex]
          : -1

      // Build new stops (excluding first stop):
      // - Keep everything up to and including the previous non-passthrough stop
      // - Add the new leg's stop (replacing the current stop and any passthroughs before it)
      // - Keep everything after the current stop
      const newRemainingStops: RouteStopWithHandle[] = [
        ...remainingStops.slice(0, prevStopActualIndex + 1),
        leg.leg.stop,
        ...remainingStops.slice(actualStopIndex + 1),
      ]

      const carConfig = getCurrentCarConfig()

      setIsFetchingPolylines(true)
      try {
        const result = await AdHocCommands.getRoutePlan(
          window.XMPP!,
          `on-page@${window.XMPP!.options.domain}`,
          [firstStop, ...newRemainingStops],
          {
            ...carConfig,
            avoid: selectedAvoidOptions,
            loadType: selectedLoadType,
          },
        )

        const customRoad = PlanRouteParser.parse(result)

        if (!customRoad?.alternativeRoutes?.[0]) {
          return
        }

        // Create custom route info
        const customRouteInfo: RouteInfo = {
          routes: [
            {
              ...(customRoad.alternativeRoutes[0].routeInfo.routes[0] || {}),
              id: "Custom",
              label: "Custom",
              isCustom: true,
            },
          ],
        }

        const customAlternativeRoute: AlternativeRoute = {
          type: "Custom",
          stops: customRoad.stops as RouteStopWithHandle[],
          routeInfo: customRouteInfo,
          tolls: customRoad.alternativeRoutes[0].tolls,
          fuel: customRoad.alternativeRoutes[0].fuel,
        }

        // Remove any existing Custom route and add the new one
        const nonCustomAlternatives = alternativeRoutes.filter(
          (alt) => !alt.routeInfo.routes[0].isCustom,
        )

        // Add custom route to alternatives array
        setAlternativeRoutes([customAlternativeRoute, ...nonCustomAlternatives])

        // Set as selected route - preserve first stop
        setTemporaryRoute({
          ...temporaryRoute,
          stops: [firstStop, ...customRoad.stops],
          routeId: "Custom",
          tolls: customRoad.alternativeRoutes[0].tolls,
        })
        setTemporaryRouteInfo(customRouteInfo)

        // Track alternative leg selection
        window.analytics?.track(
          window.anonymousXMPP
            ? "Anonymous_AlternativeLegSelected"
            : "OrderOnPage_AlternativeLegSelected",
          {
            legIndex,
          },
        )
      } catch (err) {
        console.error(`Error fetching custom route:`, err)
      } finally {
        setIsFetchingPolylines(false)
      }
    },
    [
      temporaryRoute,
      alternativeRoutes,
      uniqueAlternativeLegs,
      getCurrentCarConfig,
      selectedAvoidOptions,
      selectedLoadType,
    ],
  )

  const selectedRouteId = temporaryRouteInfo?.routes?.[0]?.id
  const hoverRouteVisible = Boolean(
    hoveredRouteId && hoveredRouteId !== selectedRouteId && !isDraggingRoute,
  )

  // Hide alternatives when dragging route to add passthrough
  const showAlternatives = alternativeRoutesEnabled && !isDraggingRoute

  const routingProvider = useSelector(selectRoutingProvider)
  const isHereRouting = useSelector(selectIsHereRouting)
  const summaryText = getOptionsSummaryText({
    stops: temporaryRoute?.stops,
    avoid: selectedAvoidOptions,
    loadType: selectedLoadType,
    routingProvider,
  })

  // Determine if clear route button should be shown
  // show if there is a temporary route or any options selected or car
  const showClearRouteButton =
    temporaryRoute ||
    selectedAvoidOptions.length > 0 ||
    selectedLoadType.length > 0 ||
    selectedVehicleType !== "tir" ||
    Boolean(presetFuelConsumption)

  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)
  const hasSidebar = Boolean(window.XMPP)
  const isAnonymous = Boolean(window.anonymousXMPP)
  // On a phone-width viewport there isn't room to split the order panel and
  // the map side by side without squeezing both unreadable — stack them
  // instead, same panels, just a vertical PanelGroup.
  const isMobile = useIsMobile()

  // Phone: size the order panel to its content (header + stops + options +
  // message row) so no blank strip sits between the vehicle card and the
  // buttons — whatever is left goes to the map. Re-fits when the content
  // grows (a stop added, the message unfolded); the user can still drag.
  useEffect(() => {
    if (!isMobile) return
    const container = routeContainerRef.current
    const viewport = container?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    )
    const content = viewport?.firstElementChild as HTMLElement | null
    const groupEl = container?.closest<HTMLElement>("[data-panel-group]")
    const messageArea = messageAreaRef.current
    if (!content || !groupEl || typeof ResizeObserver === "undefined") return

    const fit = () => {
      const groupHeight = groupEl.clientHeight
      if (!groupHeight) return
      const total =
        (panelHeaderRef.current?.offsetHeight ?? 0) +
        content.offsetHeight +
        (messageArea?.offsetHeight ?? 0) +
        12 // column gap + resize handle
      const pct = Math.min(80, Math.max(25, (total / groupHeight) * 100))
      topPanelRef.current?.resize(pct)
    }
    const observer = new ResizeObserver(fit)
    observer.observe(content)
    observer.observe(groupEl)
    if (messageArea) observer.observe(messageArea)
    fit()
    return () => observer.disconnect()
  }, [isMobile])

  // The anonymous page swaps its 48px left rail for a bottom tab bar on
  // phones (AnonymousSidebar), so there this view runs full width and stops
  // 3.5rem above the bottom edge. In the product the rail is always there.
  const frameClass = !hasSidebar
    ? "inset-0 w-screen h-screen"
    : isAnonymous
      ? "inset-x-0 top-0 bottom-14 h-[calc(100dvh-3.5rem)] w-screen md:left-[49px] md:bottom-0 md:h-screen md:w-[calc(100vw-48px)]"
      : "inset-0 left-[49px] h-screen w-[calc(100vw-48px)]"

  // Vehicle / avoid / load-type selects plus the options summary and the
  // vehicle-toll card. Desktop: pinned above the stops. Phone: scrolls along
  // with the stops (below them), so these ~200px don't squeeze the stop list
  // to nothing in a half-height panel.
  const sidePad = isMobile ? "" : "px-4"
  const optionsBlock = (
    <>
      <div className="w-full overflow-x-auto overscroll-x-contain">
        <div className={`flex w-max min-w-full gap-2 py-2 ${sidePad}`}>
          <div className="flex-shrink-0">
            <VehicleTypeSelect
              key={isUsingDriverConfig ? "from-driver" : "manual-select"}
              value={isUsingDriverConfig ? undefined : selectedVehicleType}
              disabled={haveFirstStopAsDriverLocation}
              onValueChange={handleVehicleTypeChange}
              placeholder={
                isUsingDriverConfig
                  ? t("planOrder.planner.driverCar")
                  : undefined
              }
            />
          </div>
          <div className="flex-shrink-0">
            <AvoidSelect
              onClose={handleAvoidSelectClose}
              initialSelected={selectedAvoidOptions}
            />
          </div>
          <div className="flex-shrink-0">
            <LoadTypeSelect
              onClose={handleLoadTypeSelectClose}
              initialSelected={selectedLoadType}
            />
          </div>
          {inProduct && (
            <div className="w-48 shrink-0">
              <DriverSelect
                value={selectedDriver?.driverJid}
                onValueChange={handleDriverSelection}
              />
            </div>
          )}
        </div>
      </div>

      {/* Route options summary */}
      {summaryText && (
        <div className={sidePad}>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-600 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {summaryText}
            </p>
          </div>
        </div>
      )}

      {/* Vehicle type info */}
      <div className={`flex w-full gap-2 *:flex-1 ${sidePad}`}>
        <CollapsibleVehicleTollInfo
          config={getCurrentCarConfig()}
          tolls={temporaryRouteInfo?.routes?.[0]?.tolls}
          fuel={temporaryRouteInfo?.routes?.[0]?.fuel}
          showFuel={isHereRouting}
          onFuelConsumptionChange={
            isHereRouting && !isUsingDriverConfig
              ? handleFuelConsumptionChange
              : undefined
          }
        />
      </div>
    </>
  )

  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <div className={`${frameClass} fixed z-[100] bg-[var(--panel-color)]`}>
        {onSwitchToAnalyzeOrder && (
          <OrderFileDropZone onFileDropped={handleOrderFileDropped} />
        )}
        <ResizablePanelGroup
          // react-resizable-panels only computes each panel's defaultSize
          // once, at registration — a defaultSize/minSize/maxSize change on
          // an already-registered panel is a no-op (verified in a live
          // browser: crossing the breakpoint on an already-mounted group
          // flips direction correctly but silently keeps the old split). A
          // key on the mobile/desktop breakpoint forces a remount so
          // crossing it re-registers the panels with the new direction's
          // sizes.
          key={isMobile ? "mobile" : "desktop"}
          direction={isMobile ? "vertical" : "horizontal"}
          className="max-w-screen h-full"
        >
          <ResizablePanel
            ref={topPanelRef}
            className="relative flex flex-col bg-[var(--panel-color)]"
            defaultSize={isMobile ? 50 : 35}
            maxSize={isMobile ? 80 : 50}
            minSize={isMobile ? 25 : 25}
          >
            {isFetchingPolylines && (
              <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center space-y-4">
                  <Spinner size={25} />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t("planOrder.planner.fetchingRoute")}
                  </p>
                </div>
              </div>
            )}
            <div
              ref={panelHeaderRef}
              className="flex w-full flex-col px-4 pb-1 pt-3 md:p-4 md:pb-2"
            >
              {isAnonymous && <ReturningAccountBar />}
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold md:text-2xl">
                  {t("planOrder.planner.title")}
                </h1>
                {/* Saved routes selector and clear route button */}
                <div className="flex items-center gap-2">
                  {showClearRouteButton && (
                    <ClearRouteDialog
                      onConfirm={handleConfirmClear}
                      disabled={isFetchingPolylines}
                    />
                  )}
                  <SavedRoutesSelector
                    onLoadSavedRoute={onLoadSavedRoute}
                    disabled={isFetchingPolylines}
                  />
                </div>
              </div>
              {tabBar && <div className="mt-2">{tabBar}</div>}
              {isAnonymous && <PublicModeChip />}
            </div>
            <div
              ref={routeContainerRef}
              className="relative z-10 flex w-full flex-1 flex-col overflow-hidden bg-[var(--panel-color)]"
            >
              <div className="flex h-full w-full flex-col gap-2">
                {!isMobile && optionsBlock}

                <div className="flex min-h-0 flex-1 flex-col">
                  <ScrollAreaWithShadow className="flex-1 px-4">
                    <div className="w-full overflow-x-auto overscroll-x-contain">
                      <div className="flex w-max min-w-full justify-items-center gap-2">
                        <RouteStopList
                          driverJid="on-page"
                          isReadOnlyMap={false}
                          isAnalyzeOrderMap
                          routeFromAnalyzeOrder={temporaryRoute}
                          onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                          temporaryAvoidOptions={selectedAvoidOptions}
                          onEditModeChange={handleEditModeChange}
                        />
                        {temporaryRoute === undefined && !isInEditMode && (
                          <div className="flex w-full gap-2">
                            {onSwitchToAnalyzeOrder && (
                              <Button
                                variant="ghost"
                                className="h-[31px] flex-1"
                                onClick={handleSwitchToAnalyzeOrder}
                              >
                                <FilePlus2 />
                                {t("planOrder.planner.addTransportOrder")}
                              </Button>
                            )}
                            {onSwitchToTextOrder && (
                              <Button
                                variant="ghost"
                                className="h-[31px] flex-1"
                                onClick={handleSwitchToTextOrder}
                              >
                                <FileText />
                                {t("planOrder.planner.addTextOrder")}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {isMobile && (
                      <div className="flex flex-col gap-2 pb-2">
                        {optionsBlock}
                      </div>
                    )}
                  </ScrollAreaWithShadow>
                  {isReadDeliveryEnabled &&
                    onSwitchToAnalyzeOrder &&
                    temporaryRoute === undefined &&
                    !isInEditMode && (
                      <div className="flex flex-1 items-start justify-center">
                        <Button
                          variant="outline"
                          size="lg"
                          className="gap-2 text-muted-foreground"
                          onClick={handleSwitchToAnalyzeOrder}
                        >
                          <ScanSearch className="h-5 w-5" />
                          {t("planOrder.planner.readTransportOrder")}
                        </Button>
                      </div>
                    )}
                  {temporaryRoute === undefined ||
                  (temporaryRoute.stops && temporaryRoute.stops.length < 2) ? (
                    <div className="px-4 py-2 text-xs">
                      {t("planOrder.planner.needTwoStops")}
                    </div>
                  ) : null}

                  {/* Message input area - fixed to bottom */}
                  <div
                    ref={messageAreaRef}
                    className={cn(
                      "w-full border-t bg-[var(--panel-color)] dark:bg-gray-900",
                      isMobile ? "p-2" : "p-4",
                    )}
                  >
                    <div className="flex w-full flex-col gap-2">
                      {/* Kept mounted while folded: the regenerate-on-stop-change
                          effect reads textareaRef.current.value. */}
                      <div
                        className={cn(
                          "flex-grow",
                          isMobile && !isMessageOpen && "hidden",
                        )}
                      >
                        <Textarea
                          ref={textareaRef}
                          value={messageText}
                          onChange={handleDriverTextChange}
                          placeholder={t(
                            "planOrder.planner.messagePlaceholder",
                          )}
                          className={cn(
                            "font-inherit resize-none md:text-sm",
                            isMobile
                              ? "max-h-[35vh] min-h-[120px]"
                              : "max-h-[300px] min-h-[25vh]",
                          )}
                        />
                      </div>

                      <div className="flex items-center gap-2 overflow-x-auto overscroll-x-contain">
                        {!window.anonymousXMPP && (
                          <Button variant="ghost" onClick={() => handleClose()}>
                            {t("common.close")}
                          </Button>
                        )}
                        {isMobile && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2"
                            aria-expanded={isMessageOpen}
                            onClick={() => setIsMessageOpen((open) => !open)}
                          >
                            <MessageSquareText className="h-4 w-4" />
                            {t("planOrder.planner.message")}
                            {isMessageOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronUp className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <div className="flex-1" />
                        {!isMarcosMode && (
                          <Button
                            disabled={!temporaryRoute || isGeneratingShareLink}
                            variant="outline"
                            size={isMobile ? "icon" : "default"}
                            aria-label={t("planOrder.planner.shareRoute")}
                            onClick={shareRoute}
                          >
                            {isGeneratingShareLink ? (
                              <Spinner
                                size={16}
                                className={isMobile ? "" : "mr-2"}
                              />
                            ) : shareSuccessAnimate ? (
                              <Check
                                className={cn(
                                  "h-4 w-4 duration-200 animate-in zoom-in",
                                  !isMobile && "mr-2",
                                )}
                              />
                            ) : (
                              <Share2
                                className={cn(
                                  "h-4 w-4 transition-transform hover:scale-110",
                                  !isMobile && "mr-2",
                                )}
                              />
                            )}
                            {!isMobile && t("planOrder.planner.shareRoute")}
                          </Button>
                        )}
                        <Button
                          disabled={!temporaryRoute}
                          className={cn(
                            "bg-blue-600 text-white hover:bg-blue-700",
                            isMobile ? "px-3" : "px-6",
                          )}
                          onClick={copyMessage}
                        >
                          {copySuccessAnimate ? (
                            <Check className="mr-2 h-4 w-4 duration-200 animate-in zoom-in" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4 transition-transform hover:scale-110" />
                          )}{" "}
                          {t("planOrder.planner.copyMessage")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle className="z-[1001]" />
          <ResizablePanel defaultSize={isMobile ? 45 : 75}>
            <div className="relative z-[1000] flex h-full items-center justify-center">
              <MapView
                isAnalyzeOrderMap
                temporaryRoute={temporaryRoute}
                temporaryRouteInfo={temporaryRouteInfo}
                temporaryAvoidOptions={selectedAvoidOptions}
                temporaryLoadType={selectedLoadType}
                temporaryCarConfig={getCurrentCarConfig()}
                onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                onRouteDragChange={handleRouteDragChange}
                driverJid="on-page"
                isFullScreen
              >
                {/* Show single stop marker when there's only one stop and no route yet */}
                {temporaryRoute?.stops?.length === 1 && !temporaryRouteInfo && (
                  <SingleStopMarker stop={temporaryRoute.stops[0]} />
                )}
                {/* Render unique alternative legs - only when feature is enabled and not dragging */}
                {showAlternatives &&
                  uniqueAlternativeLegs
                    .filter(
                      (legData) => !legsWithPassthroughs.has(legData.legIndex),
                    )
                    .map((legData) => (
                      <AlternativeLeg
                        key={legData.id}
                        leg={legData.leg}
                        legIndex={legData.legIndex}
                        id={legData.id}
                        visible={true}
                        hoverRouteVisible={hoverRouteVisible}
                        onLegClick={handleLegClick}
                        routeLabel={legData.routeId}
                      />
                    ))}
                {showAlternatives && hoverRouteVisible && hoveredRouteId && (
                  <HoveredRouteHighlight
                    routeId={hoveredRouteId}
                    alternativeRoutes={alternativeRoutes}
                  />
                )}
                {/* Alternative Route Selector - only when feature is enabled and not dragging */}
                {showAlternatives && (
                  <AlternativeRouteSelector
                    selectedRouteId={temporaryRouteInfo?.routes?.[0]?.id}
                    alternativeRoutes={alternativeRoutes}
                    onRouteSelect={handleRouteSelect}
                    onRouteHover={handleRouteHover}
                    hoveredRouteId={hoveredRouteId}
                    className="md:mb-4"
                    disabled={isFetchingPolylines}
                    showFuel={isHereRouting}
                  />
                )}
              </MapView>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </APIProvider>
  )
}

export default PlanOrderOnPage
